import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Package, Check, CheckCircle2, Search } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import MedicationCard from '@/components/shared/MedicationCard';
import { ProgramDetailsProps } from './types';
import { ProgramOption } from '@/services/storefrontService';
import { getInitials } from './constants';

/** Format cents to dollar string */
const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const frequencyLabels: Record<string, string> = {
  weekly: '/week',
  monthly: '/month',
  quarterly: '/quarter',
};

/** Protocol option card for protocol_showcase programs */
const ProtocolOptionCard: React.FC<{
  option: ProgramOption;
  isSelected: boolean;
  onSelect: (option: ProgramOption) => void;
}> = ({ option, isSelected, onSelect }) => (
  <Card
    className={`group cursor-pointer transition-all duration-200 ${
      isSelected
        ? 'border-blue-500 bg-blue-50/50 ring-2 ring-blue-500/20'
        : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
    }`}
    onClick={() => onSelect(option)}
  >
    <CardContent className='p-4'>
      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0 flex-1'>
          <div className='flex items-center gap-2'>
            <h4 className='text-sm font-semibold text-slate-900'>{option.label}</h4>
            {option.is_subscription ? (
              <span className='inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20'>
                Recurring
              </span>
            ) : (
              <span className='inline-flex items-center rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20'>
                One-Time
              </span>
            )}
          </div>
          {option.description && (
            <p className='mt-1 text-xs text-slate-500'>{option.description}</p>
          )}
          {option.includes_services && option.includes_services.length > 0 && (
            <div className='mt-2'>
              <p className='text-[10px] font-medium uppercase tracking-wider text-slate-400'>
                Includes
              </p>
              <ul className='mt-1 space-y-0.5'>
                {option.includes_services.map((service, idx) => (
                  <li key={idx} className='flex items-center gap-1.5 text-xs text-slate-600'>
                    <Check className='h-3 w-3 flex-shrink-0 text-emerald-500' />
                    {service}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className='flex-shrink-0 text-right'>
          <p className='text-lg font-bold text-slate-900'>
            {formatCurrency(option.program_price)}
          </p>
          <p className='text-[11px] text-slate-500'>
            {option.is_subscription && option.subscription_frequency
              ? frequencyLabels[option.subscription_frequency] || ''
              : 'one-time'}
          </p>
        </div>
      </div>
      {isSelected && (
        <div className='mt-3 flex items-center gap-1.5 border-t border-blue-200 pt-2 text-xs font-medium text-blue-600'>
          <CheckCircle2 className='h-3.5 w-3.5' />
          Selected
        </div>
      )}
    </CardContent>
  </Card>
);

const ProgramDetails: React.FC<ProgramDetailsProps & {
  onProtocolOptionSelect?: (option: ProgramOption) => void;
  selectedProtocolOptionId?: string | null;
}> = ({
  program,
  medications,
  selectedMedications,
  defaultMedicationIds,
  onBack,
  onMedicationClick,
  onMedicationRemove,
  loading,
  onProtocolOptionSelect,
  selectedProtocolOptionId,
}) => {
  const [medicationSearch, setMedicationSearch] = useState('');
  const selectedCount = Object.keys(selectedMedications).length;
  const isProtocolShowcase = program?.display_mode === 'protocol_showcase';
  const protocolOptions = program?.program_options || [];

  useEffect(() => {
    setMedicationSearch('');
  }, [program?.id]);

  const normalizedSearch = medicationSearch.trim().toLowerCase();
  const matchesMedicationSearch = (med: any) => {
    if (!normalizedSearch) return true;
    const doseText = Array.isArray(med?.doses) ? med.doses.join(' ') : String(med?.doses || '');
    const searchableText = `${med?.name || ''} ${doseText}`.toLowerCase();
    return searchableText.includes(normalizedSearch);
  };

  // Filter medications from this program that are selected
  const selectedMedicationsFromProgram = medications.filter(med => 
    selectedMedications[med.id]
  );

  // Filter medications that are not selected
  const unselectedMedications = medications.filter(med => 
    !selectedMedications[med.id]
  );
  const filteredSelectedMedications = selectedMedicationsFromProgram.filter(matchesMedicationSearch);
  const filteredUnselectedMedications = unselectedMedications.filter(matchesMedicationSearch);


  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Program Header Skeleton */}
        <Skeleton className='h-28 w-full rounded-xl' />
        
        {/* Section Header Skeleton */}
        <div className='space-y-2'>
          <Skeleton className='h-6 w-48' />
          <Skeleton className='h-4 w-32' />
        </div>
        
        {/* Products Grid Skeleton */}
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className='space-y-3'>
              <Skeleton className='aspect-[4/3] w-full rounded-xl' />
              <Skeleton className='h-5 w-3/4' />
              <Skeleton className='h-4 w-1/2' />
              <Skeleton className='h-6 w-1/3' />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      {/* Back button */}
      <Button
        variant='ghost'
        size='sm'
        className='-ml-2 text-slate-600 hover:text-slate-900'
        onClick={onBack}
      >
        <ArrowLeft className='mr-2 h-4 w-4' />
        Back to Programs
      </Button>

      {/* Program Header */}
      <div className='rounded-lg border border-primary/10 bg-gradient-to-r from-primary/5 to-primary/10 p-4'>
        <div className='flex items-start gap-3'>
          {program.image_url ? (
            <div className='h-11 w-11 flex-shrink-0 overflow-hidden rounded-lg border border-slate-100'>
              <img
                src={program.image_url}
                alt={program.title}
                className='h-full w-full object-cover'
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.parentElement!.classList.add('bg-primary/20');
                  e.currentTarget.parentElement!.innerHTML = `<span class="text-primary font-montserrat text-base font-semibold flex items-center justify-center h-full w-full">${getInitials(program.title)}</span>`;
                }}
              />
            </div>
          ) : (
            <div className='flex h-11 w-11 items-center justify-center rounded-lg bg-primary/20'>
              <span className='font-montserrat text-base font-semibold text-primary'>
                {getInitials(program.title)}
              </span>
            </div>
          )}
          <div className='flex-1'>
            <div className='flex items-center gap-2'>
              <h3 className='text-base font-semibold text-slate-900'>{program.title}</h3>
              {isProtocolShowcase && (
                <span className='inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20'>
                  Protocol
                </span>
              )}
            </div>
            <p className='mt-0.5 text-sm text-slate-600'>{program.subtitle}</p>
            <p className='mt-2 line-clamp-2 text-xs text-slate-500'>{program.description}</p>
          </div>
        </div>

        {/* Selection Summary */}
        {(selectedCount > 0 || selectedProtocolOptionId) && (
          <div className='mt-4 border-t border-primary/10 pt-3'>
            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <Check className='h-4 w-4 text-primary' />
                <span className='text-sm font-medium text-slate-900'>
                  {isProtocolShowcase
                    ? 'Protocol option selected'
                    : `${selectedCount} medication${selectedCount !== 1 ? 's' : ''} selected`}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Protocol Options Section (for protocol_showcase programs) */}
      {isProtocolShowcase && protocolOptions.length > 0 && (
        <div className='space-y-4'>
          <div>
            <h4 className='font-montserrat text-lg font-bold tracking-tight text-slate-900'>
              Select a Plan
            </h4>
            <p className='mt-1 text-sm text-slate-500'>
              Choose from {protocolOptions.length} available option{protocolOptions.length !== 1 ? 's' : ''}
            </p>
          </div>

          <div className='grid grid-cols-1 gap-3'>
            {[...protocolOptions]
              .sort((a, b) => a.sort_order - b.sort_order)
              .map(option => (
                <ProtocolOptionCard
                  key={option.id}
                  option={option}
                  isSelected={selectedProtocolOptionId === option.id}
                  onSelect={(opt) => onProtocolOptionSelect?.(opt)}
                />
              ))}
          </div>
        </div>
      )}

      {/* Protocol empty state */}
      {isProtocolShowcase && protocolOptions.length === 0 && (
        <div className='py-8 text-center text-slate-500'>
          <Package className='mx-auto mb-2 h-8 w-8 text-slate-300' />
          <p className='text-sm'>No protocol options configured for this program.</p>
        </div>
      )}

      {/* Medication Sections (for medication_select programs) */}
      {!isProtocolShowcase && (
        <>
          {/* Selected Medications Section */}
          {filteredSelectedMedications.length > 0 && (
            <div className='space-y-4'>
              <div>
                <h4 className='font-montserrat text-lg font-bold tracking-tight text-slate-900'>
                  Selected Medications
                </h4>
                <p className='mt-1 text-sm text-slate-500'>
                  {filteredSelectedMedications.length} medication{filteredSelectedMedications.length !== 1 ? 's' : ''} selected
                  {normalizedSearch ? ` (filtered from ${selectedMedicationsFromProgram.length})` : ''}
                </p>
              </div>

              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                {filteredSelectedMedications.map(medication => (
                  <MedicationCard
                    key={medication.id}
                    medication={medication}
                    onClick={onMedicationClick}
                    onRemove={onMedicationRemove}
                    isDefault={defaultMedicationIds?.has(medication.id)}
                    isSelected={true}
                    selectedMedication={selectedMedications[medication.id]}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Available Medications Section */}
          <div className='space-y-4'>
            <div className='flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
              <div>
                <h4 className='font-montserrat text-lg font-bold tracking-tight text-slate-900'>
                  Available Medications
                </h4>
                <p className='mt-1 text-sm text-slate-500'>
                  Select from {filteredUnselectedMedications.length}
                  {normalizedSearch ? ` of ${unselectedMedications.length}` : ''} available products
                </p>
              </div>
              <div className='relative w-full sm:max-w-xs'>
                <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
                <Input
                  type='text'
                  value={medicationSearch}
                  onChange={e => setMedicationSearch(e.target.value)}
                  placeholder='Search medications...'
                  className='pl-9'
                />
              </div>
            </div>

            {medications.length === 0 && (
              <div className='py-8 text-center text-slate-500'>
                <Package className='mx-auto mb-2 h-8 w-8 text-slate-300' />
                <p className='text-sm'>No medications found for this program.</p>
              </div>
            )}

            {filteredUnselectedMedications.length === 0 && medications.length > 0 && (
              <div className='rounded-lg border border-slate-200 bg-slate-50 p-4 text-center'>
                {normalizedSearch ? (
                  <>
                    <Package className='mx-auto mb-2 h-8 w-8 text-slate-300' />
                    <p className='text-sm text-slate-600'>No medications match your search.</p>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className='mx-auto mb-2 h-8 w-8 text-brand-teal' />
                    <p className='text-sm text-slate-600'>All medications from this program are selected!</p>
                  </>
                )}
              </div>
            )}

            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              {filteredUnselectedMedications.map(medication => (
                <MedicationCard
                  key={medication.id}
                  medication={medication}
                  onClick={onMedicationClick}
                  isDefault={defaultMedicationIds?.has(medication.id)}
                  isSelected={false}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProgramDetails;

